import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";

import StockImage from "assets/img/stock.jpg";
import { useTranslation } from "react-i18next";

/**
 * Pantalla "Sobre nosotros".
 *
 * Objetivos de esta versión:
 * - eliminar width/height rígidos
 * - centrar el contenido con maxWidth en vez de porcentajes fijos
 * - hacer la imagen completamente responsive
 * - mantener una lectura cómoda en móvil y desktop
 */
const useStyles = makeStyles((theme) => ({
  /**
   * Wrapper general de la pantalla.
   *
   * Antes:
   * - width: 60%
   * - height: 80vh
   *
   * Problema:
   * - en móvil quedaba demasiado estrecho o forzado
   * - en pantallas bajas podía generar layouts incómodos
   *
   * Ahora:
   * - ancho completo
   * - maxWidth controlado
   * - altura natural
   * - padding responsive
   */
  wrapper: {
    width: "100%",
    maxWidth: 980,
    margin: "0 auto",
    padding: theme.spacing(2),

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
  },

  /**
   * Título de la card.
   */
  cardTitle: {
    fontWeight: 500,
    fontSize: "1.5rem",

    [theme.breakpoints.down("sm")]: {
      fontSize: "1.25rem",
    },
  },

  /**
   * Contenido interior de la card.
   * Se centra y se deja en columna para una lectura limpia.
   */
  cardBody: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(2.5),

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },

  /**
   * Imagen principal.
   *
   * Antes se centraba con paddingLeft manual.
   * Ahora:
   * - se centra con display block + margin auto
   * - ocupa todo el ancho disponible hasta un máximo razonable
   */
  image: {
    display: "block",
    width: "100%",
    maxWidth: 720,
    height: "auto",
    margin: "0 auto",
    borderRadius: 6,
    objectFit: "cover",
  },

  /**
   * Texto descriptivo.
   * Ancho cómodo y centrado.
   */
  description: {
    marginTop: theme.spacing(2),
    width: "100%",
    maxWidth: 820,
    textAlign: "center",
    lineHeight: 1.7,
    padding: theme.spacing(0, 1),

    [theme.breakpoints.down("sm")]: {
      fontSize: "0.95rem",
      lineHeight: 1.65,
      padding: theme.spacing(0, 0.5),
    },
  },

  /**
   * Bloque de versión.
   * Lo dejamos debajo de la card con separación clara.
   */
  version: {
    marginTop: theme.spacing(1.5),
    textAlign: "center",
    fontSize: "0.9rem",
    opacity: 0.75,
  },
}));

export default function About() {
  const classes = useStyles();
  const { t } = useTranslation("aboutUs");

  return (
    <GridContainer justify="center" className={classes.wrapper}>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="primary">
            <Typography variant="h5" className={classes.cardTitle}>
              {t("title")}
            </Typography>
          </CardHeader>

          <CardBody className={classes.cardBody}>
            <img
              src={StockImage}
              alt={t("image-alt", { defaultValue: "Stock" })}
              className={classes.image}
            />

            <Typography variant="body1" className={classes.description}>
              {t("description")}
            </Typography>
          </CardBody>
        </Card>

        <Typography
          variant="caption"
          color="textSecondary"
          className={classes.version}
        >
          1.0.6-release
        </Typography>
      </GridItem>
    </GridContainer>
  );
}